/**
 * ============================================================================
 * СЕРВИС МОДУЛЕЙ
 * ============================================================================
 * Методы для работы с модулями курсов
 * ============================================================================
 */

import api from './api'
import { compressImage, shouldCompress } from '@/utils/imageCompression'

export const modulesService = {
	/**
	 * Получить все модули курса
	 */
	async getByCourseId(courseId, includeUnpublished = false) {
		const params = includeUnpublished ? { includeUnpublished: 'true' } : undefined
		const response = await api.get(`/courses/${courseId}/modules`, { params })
		return response.data
	},

	/**
	 * Получить модуль по ID
	 */
	async getById(id) {
		const response = await api.get(`/modules/${id}`)
		return response.data
	},

	/**
	 * Создать новый модуль
	 */
	async create(courseId, moduleData) {
		const response = await api.post(`/courses/${courseId}/modules`, moduleData)
		return response.data
	},

	/**
	 * Обновить модуль
	 */
	async update(id, moduleData) {
		const response = await api.put(`/modules/${id}`, moduleData)
		return response.data
	},

	/**
	 * Удалить модуль
	 */
	async delete(id) {
		const response = await api.delete(`/modules/${id}`)
		return response.data
	},

	/**
	 * Опубликовать модуль
	 */
	async publish(id) {
		const response = await api.patch(`/modules/${id}/publish`)
		return response.data
	},

	/**
	 * Снять с публикации
	 */
	async unpublish(id) {
		const response = await api.patch(`/modules/${id}/unpublish`)
		return response.data
	},

	/**
	 * Изменить порядок модулей
	 */
	async reorder(courseId, moduleIds) {
		const response = await api.post(`/courses/${courseId}/modules/reorder`, {
			moduleIds,
		})
		return response.data
	},

	/**
	 * Загрузить обложку модуля
	 */
	async uploadCover(moduleId, file) {
		// Сжимаем изображение если оно больше 1MB
		let fileToUpload = file
		if (shouldCompress(file, 1)) {
			console.log(`🗜️ Сжатие изображения: ${(file.size / 1024 / 1024).toFixed(2)}MB`)
			fileToUpload = await compressImage(file, {
				maxWidth: 1920,
				maxHeight: 1080,
				quality: 0.85,
				maxSizeMB: 1,
			})
			console.log(`✅ Сжато до: ${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`)
		}

		const formData = new FormData()
		formData.append('cover', fileToUpload)

		const response = await api.post(`/modules/${moduleId}/cover`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data
	},

	/**
	 * Получить полные данные модуля со всеми элементами
	 */
	async getModuleWithElements(moduleId) {
		try {
			// Получаем базовую информацию о модуле
			const module = await this.getById(moduleId)

			// Параллельно загружаем все элементы модуля
			const extractArrayPayload = payload => {
				if (Array.isArray(payload)) return payload
				if (Array.isArray(payload?.data)) return payload.data
				return []
			}

			const loadSection = async (path, sectionName) => {
				try {
					const payload = await api.get(path)
					return extractArrayPayload(payload)
				} catch (error) {
					console.warn(
						`Не удалось загрузить раздел "${sectionName}" модуля ${moduleId}:`,
						error,
					)
					return []
				}
			}

			const lessons = await loadSection(`/modules/${moduleId}/lessons`, 'lessons')
			const tests = await loadSection(`/modules/${moduleId}/tests`, 'tests')
			const homework = await loadSection(`/modules/${moduleId}/homework`, 'homework')
			const practice = await loadSection(`/modules/${moduleId}/practical`, 'practical')

			return {
				...module,
				lessons: lessons || [],
				tests: tests || [],
				homework: homework || [],
				practice: practice || [],
			}
		} catch (error) {
			console.error('Ошибка загрузки данных модуля:', error)
			throw error
		}
	},

	/**
	 * Получить все модули курса с элементами
	 */
	async getCourseModulesWithElements(courseId) {
		try {
			// 👇 СНАЧАЛА ПЫТАЕМСЯ ЗАГРУЗИТЬ ИЗ LOCALSTORAGE
			const localModules = this.loadModulesFromLocalStorage(courseId)
			if (localModules && localModules.length > 0) {
				console.log(
					`✅ Загружено ${localModules.length} модулей из localStorage для курса ${courseId}`
				)
				return localModules
			}

			// 👇 ЕСЛИ НЕТ В LOCALSTORAGE - ЗАГРУЖАЕМ С СЕРВЕРА
			console.log(
				`ℹ️ Нет данных в localStorage, загружаем с сервера для курса ${courseId}`
			)

			// Получаем список модулей
			const modules = await this.getByCourseId(courseId)

			// Загружаем элементы для каждого модуля
			const modulesWithElements = await Promise.all(
				modules.map(module => this.getModuleWithElements(module.id))
			)

			return modulesWithElements
		} catch (error) {
			console.error('Ошибка при получении модулей курса:', error)

			// 👇 ЕСЛИ ОШИБКА API - ПЫТАЕМСЯ ЗАГРУЗИТЬ ИЗ LOCALSTORAGE КАК FALLBACK
			const localModules = this.loadModulesFromLocalStorage(courseId)
			if (localModules && localModules.length > 0) {
				console.warn(
					'⚠️ Ошибка API, используем данные из localStorage как fallback'
				)
				return localModules
			}

			throw new Error('Ошибка при получении модулей курса')
		}
	},

	/**
	 * Загрузить модули курса из localStorage
	 */
	loadModulesFromLocalStorage(courseId) {
		try {
			// Получаем список ID модулей для курса
			const courseModulesKey = `course_${courseId}_modules`
			const moduleIdsData = localStorage.getItem(courseModulesKey)

			if (!moduleIdsData) {
				console.log(`ℹ️ Нет сохраненных модулей для курса ${courseId}`)
				return []
			}

			const moduleIds = JSON.parse(moduleIdsData)
			console.log(
				`📦 Найдено ${moduleIds.length} ID модулей для курса ${courseId}:`,
				moduleIds
			)

			// Загружаем данные каждого модуля
			const modules = moduleIds
				.map(moduleId => {
					const storageKey = `moduleTestsSection_module_${moduleId}`
					const savedData = localStorage.getItem(storageKey)

					if (!savedData) {
						console.warn(`⚠️ Нет данных для модуля ${moduleId}`)
						return null
					}

					try {
						const parsedData = JSON.parse(savedData)

						// Формируем структуру модуля
						return {
							id: moduleId,
							title: parsedData.title || parsedData.name || '',
							subtitle: '',
							lessons: parsedData.lessons || [],
							tests: parsedData.tests || [],
							homework: parsedData.homeworks || [],
							practice: parsedData.practices || [],
						}
					} catch (parseError) {
						console.error(
							`❌ Ошибка парсинга данных модуля ${moduleId}:`,
							parseError
						)
						return null
					}
				})
				.filter(module => module !== null)

			console.log(
				`✅ Успешно загружено ${modules.length} модулей из localStorage`
			)
			return modules
		} catch (error) {
			console.error('❌ Ошибка загрузки модулей из localStorage:', error)
			return []
		}
	},

	/**
	 * Сохранить список ID модулей для курса
	 */
	saveCourseModuleIds(courseId, moduleIds) {
		try {
			const courseModulesKey = `course_${courseId}_modules`
			localStorage.setItem(courseModulesKey, JSON.stringify(moduleIds))
			console.log(`✅ Сохранены ID модулей для курса ${courseId}:`, moduleIds)
		} catch (error) {
			console.error('❌ Ошибка сохранения ID модулей:', error)
		}
	},
}

export default modulesService
